import type { TableCraftObject } from '@carboncopyco/types';
import {
  Button,
  Checkbox,
  DropdownFilter,
  Loading,
  MultiDropdownFilter,
  TextCaption,
  TextLargeParagraph,
  TooltipBubble,
} from '@carboncopyco/ui';
import classNames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { MultiDropdownRenderItem } from '../MultiDropdownRenderItem';
import { RulesManager } from '../RulesManager';
import { SearchBoxButton } from '../SearchBoxButton';
import { SearchBoxInput } from '../SearchBoxInput';
import { useMultiDropdownFilter } from '../hooks';
import { ObjectsManagerContent } from './ObjectsManagerContent';

type ModifiedFilter = 'modified' | 'unmodified' | 'all';

type ObjectsManagerProps = {
  isOpen: boolean;
  onClose: () => void;
  detailsContent: ReactNode;
  onCloseDetails: () => void;
  rawTableCraftObjects: TableCraftObject[];
  tableCraftObjects: TableCraftObject[];
  tableCraftObjectsStatus: 'loading' | 'indexing' | 'success' | 'error';
  selectedObject: TableCraftObject;
  onObjectClick: (id: TableCraftObject['structure_sha']) => void;
  onObjectHide: (
    id: TableCraftObject['structure_sha'],
    hide: boolean
  ) => Promise<void>;
  searchValue: string;
  onSearchChange: (value: string) => void;
  isVisualSearch: boolean;
  onTextSearchClick: () => void;
  onModifiedFilterChange: (value: ModifiedFilter) => void;
  modifiedFilter: ModifiedFilter;
  alignValues: boolean;
  onAlignValuesChange: () => void;
};

const MultiDropdownFilterContainer = ({
  rawTableCraftObjects,
  onToggle,
}: {
  rawTableCraftObjects: TableCraftObject[];
  onToggle: (key: string, hide: boolean) => Promise<void>;
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const entries = useMemo(() => {
    return rawTableCraftObjects
      .filter((obj) => obj.hide)
      .map((obj) => [obj.structure_sha, true]);
  }, [rawTableCraftObjects]);

  const fixedGroupItems = useMemo(() => {
    return rawTableCraftObjects.map((obj) => ({
      id: obj.structure_sha,
      label: obj.display_name,
    }));
  }, [rawTableCraftObjects]);

  const {
    selectedItems: typesFilterSelectedItems,
    onChange: onTypesFilterChange,
  } = useMultiDropdownFilter({
    fixedGroupItems,
    selectedItems: Object.fromEntries(entries),
  });

  return (
    <div className="min-w-0 flex flex-col grow shrink">
      <MultiDropdownFilter
        placeholder="Hidden"
        fixedGroup={{
          items: rawTableCraftObjects.map((obj) => ({
            id: obj.structure_sha,
            label: obj.display_name,
          })),
        }}
        header="Hidden database tables"
        renderItem={(props) => (
          <MultiDropdownRenderItem
            {...props}
            isDisabled={isLoading}
          />
        )}
        selectedItems={typesFilterSelectedItems}
        onChange={async (item) => {
          onTypesFilterChange(item);
          setIsLoading(true);
          onToggle(item, !typesFilterSelectedItems[item]).then(() =>
            setIsLoading(false)
          );
        }}
        footer={<></>}
      />
    </div>
  );
};

export const ObjectsManager = ({
  isOpen,
  onClose,
  detailsContent,
  onCloseDetails,
  selectedObject,
  rawTableCraftObjects,
  tableCraftObjects,
  tableCraftObjectsStatus,
  onObjectClick,
  onObjectHide,
  searchValue,
  onSearchChange,
  isVisualSearch,
  onTextSearchClick,
  modifiedFilter,
  onModifiedFilterChange,
  alignValues,
  onAlignValuesChange,
}: ObjectsManagerProps) => {
  const [isSearchActive, setIsSearchActive] = useState(false);
  const flags = useFlags();

  useEffect(() => {
    if (!isOpen) {
      setIsSearchActive(false);
    }
  }, [isOpen]);

  return (
    <RulesManager
      isOpen={isOpen}
      onClose={onClose}
      detailsContent={detailsContent}
      onCloseDetails={onCloseDetails}
    >
      <RulesManager.Header>
        <div className="flex min-h-[36px]">
          <RulesManager.Header.Title label="Database Editing" />
        </div>
      </RulesManager.Header>
      <div className="mt-2 mb-5">
        <RulesManager.Subheader
          label="Edit your product’s database tables,"
          linkLabel="Learn more"
          linkUrl="https://help.demostack.com/articles/object-editing-1"
        />
      </div>
      {flags.editorEnrich && (
        <div className="mt-2 mb-5">
          <Checkbox
            label={
              <TextCaption
                label="Automatically align values in all tables"
                color="text-ds-natural-600"
              />
            }
            name="alignValues"
            checked={alignValues}
            onChange={onAlignValuesChange}
          />
        </div>
      )}
      <div className="mb-5">
        <div className="flex-1 flex items-center justify-between gap-1">
          {!isSearchActive && (
            <>
              <div className="inline-flex flex-col min-w-[130px]">
                <DropdownFilter<ModifiedFilter>
                  placeholder="Any type"
                  items={[
                    {
                      label: 'Any type',
                      id: 'all',
                    },
                    {
                      label: 'Modified',
                      id: 'modified',
                    },
                    {
                      label: 'Unmodified',
                      id: 'unmodified',
                    },
                  ]}
                  onSelect={onModifiedFilterChange}
                  selected={modifiedFilter}
                  contentWidth={200}
                />
              </div>

              <MultiDropdownFilterContainer
                key={selectedObject?.structure_sha}
                rawTableCraftObjects={rawTableCraftObjects ?? []}
                onToggle={(structure_sha, hide) =>
                  onObjectHide(structure_sha, hide)
                }
              />
            </>
          )}

          {isVisualSearch ? (
            <div className="flex gap-1 items-center w-full">
              <div className="flex-1">
                <SearchBoxInput
                  value="Visual search results"
                  disabled
                />
              </div>
              <TooltipBubble
                placement="top"
                title="Switch to text search"
                appendTo="parent"
              >
                <Button
                  label="T"
                  aria-label="Switch to text search"
                  small
                  secondary
                  onClick={onTextSearchClick}
                />
              </TooltipBubble>
            </div>
          ) : (
            <div
              className={classNames('flex flex-col min-w-[20px]', {
                grow: isSearchActive,
              })}
            >
              <SearchBoxButton
                value={searchValue}
                onChange={onSearchChange}
                onOpen={() => setIsSearchActive(true)}
                onClose={() => setIsSearchActive(false)}
              />
            </div>
          )}
        </div>
      </div>
      {!tableCraftObjects.length ? (
        <div className="flex flex-col items-center justify-center flex-1 gap-2.5">
          {tableCraftObjectsStatus === 'loading' && (
            <Loading color="text-ds-primary" />
          )}
          {tableCraftObjectsStatus === 'success' && (
            <>
              <TextLargeParagraph
                label="No database tables found"
                color="text-ds-natural-600"
              />
              <TextCaption
                label="Try changing your search or filter criteria"
                color="text-ds-natural-600"
              />
            </>
          )}
          {tableCraftObjectsStatus === 'error' && (
            <>
              <TextLargeParagraph
                label="An error occurred"
                color="text-ds-natural-600"
              />
              <TextCaption
                label="Please try again later"
                color="text-ds-natural-600"
              />
            </>
          )}
          {tableCraftObjectsStatus === 'indexing' && (
            <>
              <TextLargeParagraph
                label="Indexing objects"
                color="text-ds-natural-600"
              />
              <TextCaption
                label="This may take a few moments"
                color="text-ds-natural-600"
              />
            </>
          )}
        </div>
      ) : (
        <RulesManager.Content>
          <ObjectsManagerContent
            selectedObject={selectedObject}
            tableCraftObjects={tableCraftObjects}
            onObjectClick={onObjectClick}
            onObjectHide={onObjectHide}
          />
        </RulesManager.Content>
      )}
    </RulesManager>
  );
};
